import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, setDoc, getDoc, deleteDoc, writeBatch, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ROLE } from '../roles';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Database, Upload, Users, GraduationCap, Plus, Edit2, Trash2, RefreshCw, AlertTriangle, Download, ChevronUp, ChevronDown, ChevronsUpDown, Zap, Search, Filter } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';

const StudentMgt = () => {
  const { t } = useT();
  const [activeTab, setActiveTab] = useState('student');
  const [staffList, setStaffList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: 'class_classNo', direction: 'asc' });
  const [globalSearch, setGlobalSearch] = useState('');
  const [topClassFilter, setTopClassFilter] = useState('');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState({});

  const [selectedRows, setSelectedRows] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchFormData, setBatchFormData] = useState({ currentClass: '', gender: '' });

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const initialFormState = {
    email: '', lastName: '', firstName: '', chineseName: '', gender: 'Male', dob: '', password: '',
    sid: '', currentClass: '', classNumber: '', hasLoginAccess: false, // Student
    // Up to two guardians. Both may READ the child's records; only the primary signs notices and
    // authorises payments, because an acknowledgement needs one accountable signatory rather than
    // whichever parent happened to open the app first.
    guardianEmail: '', guardianPassword: '',
    guardianEmail2: '', guardianPassword2: '',
    primaryGuardian: 'first'
  };
  const [formData, setFormData] = useState(initialFormState);

  const [dropdownConfig, setDropdownConfig] = useState({ edbRanks: [], jobRoles: [] });

  const { activeSchoolId, isMobile } = useOutletContext();

  const requestAccountAction = (action, email, password, type, systemRole = '') => {
    return new Promise(async (resolve, reject) => {
      try {
        const docRef = doc(collection(db, 'schools', activeSchoolId, '_account_requests'));
        await setDoc(docRef, {
          email,
          password,
          type,
          // Without this the function can only tell staff from student and would file a guardian
          // as a Student in user_permissions -- which is the field Firestore rules match on.
          systemRole,
          action,
          status: 'PENDING',
          createdAt: serverTimestamp()
        });

        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data();
          if (data && data.status === 'SUCCESS') {
            unsubscribe();
            deleteDoc(docRef).catch(console.error);
            resolve(data.message);
          } else if (data && data.status === 'ERROR') {
            unsubscribe();
            deleteDoc(docRef).catch(console.error);
            reject(new Error(data.message));
          }
        });
      } catch (err) {
        reject(err);
      }
    });
  };

  useEffect(() => {
    setSortConfig({ key: 'class_classNo', direction: 'asc' });
    if (activeSchoolId) fetchStudents();
  }, [activeTab, activeSchoolId]);

  useEffect(() => {
    if (!activeSchoolId) return;
    const unsub = onSnapshot(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'), async (docSnap) => {
      if (docSnap.exists() && docSnap.data().dropdowns) {
        setDropdownConfig(docSnap.data().dropdowns);
      } else {
        getDoc(doc(db, 'metadata', 'system_settings')).then(globalSnap => {
          if (globalSnap.exists() && globalSnap.data().dropdowns) {
            setDropdownConfig(globalSnap.data().dropdowns);
          }
        });
      }
    });
    return () => unsub();
  }, [activeSchoolId]);

  
  const fetchStudents = async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      const profSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'student_profiles')));
      
      const data = [];
      profSnap.forEach(d => {
        const u = d.data();
        data.push({
          id: d.id,
          email: u.email || d.id,
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          chineseName: u.chineseName || '',
          sid: u.sid || '',
          currentClass: u.currentClass || '',
          classNumber: u.classNumber || '',
          gender: u.gender || 'Male',
          dob: u.dob || '',
          hasLoginAccess: !!u.hasLoginAccess
        });
      });
      setStudentList(data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    }
    return <ChevronsUpDown size={14} style={{ opacity: 0.3 }} />;
  };

  const sortedStaff = [...staffList].sort((a, b) => {
    let comparison = 0;
    if (sortConfig.key === 'jobRole_initial') {
      const roleOrder = { 'Teaching Staff': 1, 'Admin Staff': 2, 'Supporting Staff': 3 };
      const rA = roleOrder[a.jobRole] || 99;
      const rB = roleOrder[b.jobRole] || 99;
      if (rA !== rB) comparison = rA - rB;
      else comparison = (a.initial || '').localeCompare(b.initial || '');
    } else if (sortConfig.key === 'name') {
      comparison = (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '');
    } else {
      const valA = String(a[sortConfig.key] || '');
      const valB = String(b[sortConfig.key] || '');
      comparison = valA.localeCompare(valB, undefined, { numeric: true });
    }
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleFilter = (key) => {
    setShowFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredStudents = studentList.filter(student => {
    if (topClassFilter && student.currentClass !== topClassFilter) return false;
    
    if (globalSearch) {
      const q = globalSearch.toLowerCase();
      const match = [
        student.id, student.firstName, student.lastName, student.chineseName, 
        student.sid, student.currentClass, student.classNumber
      ].some(val => String(val || '').toLowerCase().includes(q));
      if (!match) return false;
    }
    for (const key in filters) {
      if (filters[key]) {
        let val = String(student[key] || '');
        if (key === 'name') {
           val = `${student.lastName || ''} ${student.firstName || ''}`;
        }
        if (!val.toLowerCase().includes(filters[key].toLowerCase())) {
          return false;
        }
      }
    }
    return true;
  });

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let comparison = 0;
    if (sortConfig.key === 'class_classNo') {
      const cA = a.currentClass || 'ZZZ';
      const cB = b.currentClass || 'ZZZ';
      if (cA !== cB) comparison = cA.localeCompare(cB);
      else {
        const nA = Number(a.classNumber) || 999;
        const nB = Number(b.classNumber) || 999;
        comparison = nA - nB;
      }
    } else if (sortConfig.key === 'name') {
      comparison = (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '');
    } else {
      const valA = String(a[sortConfig.key] || '');
      const valB = String(b[sortConfig.key] || '');
      comparison = valA.localeCompare(valB, undefined, { numeric: true });
    }
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const handleOpenEdit = (profile) => {
    setIsEditing(true);
    setFormData({
      ...initialFormState,
      ...profile,
      email: profile.id,
      // Derived, so spreading the profile does not set it: which of the two addresses is primary.
      primaryGuardian:
        profile.primaryGuardianEmail
        && profile.primaryGuardianEmail === (profile.guardianEmail2 || '')
          ? 'second'
          : 'first',
      // Never prefill a password field from stored data.
      guardianPassword: '',
      guardianPassword2: '',
    });
    setShowModal(true);
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    setFormData(initialFormState);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('studentMgt.deleteConfirm', { id }))) return;
    setLoading(true);
    try {
      await requestAccountAction('Delete Account', id, '', activeTab);
      const col = activeTab === 'staff' ? 'staff_profiles' : 'student_profiles';
      await deleteDoc(doc(db, 'schools', activeSchoolId, col, id));
      fetchStudents();
    } catch (error) {
      alert(t('studentMgt.deleteFailed', { message: error.message }));
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(sortedStudents.map(s => s.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const handleBatchDelete = async () => {
    if (!window.confirm(t('studentMgt.deleteManyConfirm', { count: selectedRows.length }))) return;
    setLoading(true);
    try {
      const batchOp = writeBatch(db);
      for (const id of selectedRows) {
        const docRef = doc(db, 'schools', activeSchoolId, 'student_profiles', id);
        batchOp.delete(docRef);
      }
      await batchOp.commit();
      selectedRows.forEach(id => requestAccountAction('Delete Account', id, '', 'student').catch(() => {}));
      setSelectedRows([]);
      fetchStudents();
    } catch (err) {
      alert(t('studentMgt.deleteFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleBatchSave = async (e) => {
    e.preventDefault();
    if (!batchFormData.currentClass && !batchFormData.gender) {
      return alert(t('studentMgt.batchNeedsField'));
    }
    setLoading(true);
    try {
      const batchOp = writeBatch(db);
      for (const id of selectedRows) {
        const docRef = doc(db, 'schools', activeSchoolId, 'student_profiles', id);
        const updates = {};
        if (batchFormData.currentClass) updates.currentClass = batchFormData.currentClass;
        if (batchFormData.gender) updates.gender = batchFormData.gender;
        batchOp.set(docRef, updates, { merge: true });
      }
      await batchOp.commit();
      setShowBatchModal(false);
      setSelectedRows([]);
      fetchStudents();
    } catch (err) {
      alert(t('studentMgt.batchFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.email) return alert(t('studentMgt.emailRequired'));
    
    const emailToSave = formData.email.trim().toLowerCase();
    if (!isEditing && studentList.some(s => s.id === emailToSave)) {
      return alert(t('studentMgt.emailInUse'));
    }

    const sidToSave = (formData.sid || '').trim();
    if (!sidToSave) return alert(t('studentMgt.sidRequired'));
    if (studentList.some(s => s.sid === sidToSave && s.id !== emailToSave)) {
      return alert(t('studentMgt.sidInUse'));
    }

    if (formData.classNumber) {
      const num = Number(formData.classNumber);
      if (isNaN(num) || num <= 0) {
        return alert(t('studentMgt.classNumberPositive'));
      }
    }

    const validGenders = ['Male', 'Female', ''];
    if (!validGenders.includes(formData.gender)) return alert(t('studentMgt.invalidGender'));
    
    try {
      setLoading(true);

      if (formData.hasLoginAccess) {
        if (!isEditing && !formData.password) {
          setLoading(false);
          return alert(t('studentMgt.passwordRequired'));
        }
        if (formData.password) {
          if (formData.password.length < 6) {
            setLoading(false);
            return alert(t('studentMgt.passwordTooShort'));
          }
          try {
            await requestAccountAction(isEditing ? 'Update' : 'Add', formData.email.trim(), formData.password.trim(), activeTab, ROLE.STUDENT);
          } catch (authErr) {
            let msg = authErr.message;
            if (msg === 'internal') {
               msg = "Server error processing the request.";
            }
            throw new Error("Account Action Failed: " + msg);
          }
        }
      } else {
        try {
          await requestAccountAction('Delete Account', formData.email.trim(), '', activeTab);
        } catch (e) {
          // Ignore if user didn't exist in auth
        }
      }

      const guardianEmail = (formData.guardianEmail || '').trim().toLowerCase();
      const guardianEmail2 = (formData.guardianEmail2 || '').trim().toLowerCase();

      if (guardianEmail && guardianEmail2 && guardianEmail === guardianEmail2) {
        alert(t('studentMgt.guardiansSame'));
        setLoading(false);
        return;
      }

      for (const [addr, pw] of [
        [guardianEmail, formData.guardianPassword],
        [guardianEmail2, formData.guardianPassword2],
      ]) {
        if (!addr || !pw) continue;
        try {
          await requestAccountAction('Add', addr, pw, 'parent', 'parent');
        } catch (e) {
          alert(t('studentMgt.guardianLoginFailed', { email: addr, message: e.message }));
        }
      }

      const col = 'student_profiles';
      const payload = {
        email: formData.email.toLowerCase(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        chineseName: formData.chineseName || '',
        gender: formData.gender,
        role: 'student',
        schoolId: activeSchoolId,
        dob: formData.dob,
        sid: formData.sid,
        currentClass: formData.currentClass,
        classNumber: String(formData.classNumber),
        hasLoginAccess: formData.hasLoginAccess,
        // The guardians' logins. These fields are the link between a parent account and a child:
        // syncSubjectClaims derives childSids from them, Layout resolves a guardian's children from
        // them, and every denormalised guardianEmails array is stamped from them.
        guardianEmail,
        guardianEmail2,
        // Only the primary may sign a notice or authorise a payment; both may read. Stored
        // explicitly rather than implied by field order, so the rule does not depend on a
        // convention someone could reasonably reorder.
        primaryGuardianEmail: formData.primaryGuardian === 'second' && guardianEmail2
          ? guardianEmail2
          : guardianEmail,
      };

      await setDoc(doc(db, 'schools', activeSchoolId, col, formData.email.toLowerCase()), payload, { merge: true });

      setShowModal(false);
      fetchStudents();
      
    } catch (error) {
      alert(t('studentMgt.saveFailed', { message: error.message }));
    }
  };

  const getJobRoleName = (code) => {
    if (!code) return 'N/A';
    const role = (dropdownConfig.jobRoles || []).find(r => r.code === code || r.nameEn === code || r.name === code);
    return role ? (role.nameEn || role.name || code) : code;
  };

  const getJobRoleCode = (input) => {
    if (!input) return 'Teaching Staff';
    const str = String(input).trim();
    const role = (dropdownConfig.jobRoles || []).find(r => r.code === str || r.nameEn === str || r.name === str);
    return role ? (role.code || str) : str;
  };


  const exportStudentCSV = () => {
    const csvData = studentList.map(u => ({
      Email: u.email,
      'Last Name': u.lastName || '',
      'First Name': u.firstName || '',
      'Chinese Name': u.chineseName || '',
      'Student ID': u.sid || '',
      'Current Class': u.currentClass || '',
      'Class Number': u.classNumber || '',
      Gender: u.gender || '',
      'Date of Birth': u.dob || '',
      'Enable Login': u.hasLoginAccess ? 'Yes' : 'No'
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "student_profiles_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const importStudentCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const buffer = evt.target.result;
      const arr = new Uint8Array(buffer);
      let encoding = 'utf-8';
      try {
        new TextDecoder('utf-8', { fatal: true }).decode(arr);
      } catch (err) {
        encoding = 'Big5';
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: encoding,
        complete: async (results) => {
          setLoading(true);
        try {
          // Pre-scan validation
          const errors = [];
          const validGenders = ['Male', 'Female', ''];

          results.data.forEach((row, index) => {
            const email = row['Email']?.trim().toLowerCase();
            if (!email) return;

            const gender = row['Gender'] || '';
            if (!validGenders.includes(gender)) {
              errors.push(t('studentMgt.rowInvalidGender', { row: index + 1, email, value: gender }));
            }
          });

          if (errors.length > 0) {
            setLoading(false);
            alert(t('studentMgt.validationFailed') + '\n\n' + errors.slice(0, 10).join('\n')
              + (errors.length > 10 ? '\n' + t('studentMgt.andMoreErrors', { count: errors.length - 10 }) : ''));
            return;
          }

          const batch = writeBatch(db);
          let count = 0;

          for (const row of results.data) {
            const email = row['Email']?.trim().toLowerCase();
            if (!email) continue;

            const enableLoginStr = (row['Enable Login'] || '').toLowerCase().trim();
            const hasLoginAccess = enableLoginStr === 'yes' || enableLoginStr === 'true' || enableLoginStr === '1';

            const rawPassword = row['Password'];
            let passwordToSet = null;
            if (rawPassword && rawPassword !== '*' && rawPassword.trim() !== '') {
              passwordToSet = rawPassword.trim();
            }

            const existingProfile = studentList.find(s => s.id === email || s.email === email);
            const isNew = !existingProfile;

            if (isNew) {
              if (hasLoginAccess) {
                 await requestAccountAction('Add', email, passwordToSet || '123456', activeTab, ROLE.STUDENT);
              }
            } else {
              if (hasLoginAccess && !existingProfile.hasLoginAccess) {
                 await requestAccountAction('Add', email, passwordToSet || '123456', activeTab, ROLE.STUDENT);
              } else if (!hasLoginAccess && existingProfile.hasLoginAccess) {
                 try {
                   await requestAccountAction('Delete Account', email, '', activeTab);
                 } catch (e) {}
              } else if (hasLoginAccess && passwordToSet) {
                 await requestAccountAction('Update', email, passwordToSet, activeTab);
              }
            }
            
            const profRef = doc(db, 'schools', activeSchoolId, 'student_profiles', email);
            batch.set(profRef, {
              email: email,
              role: 'student',
              firstName: row['First Name'] || '',
              lastName: row['Last Name'] || '',
              chineseName: row['Chinese Name'] || '',
              gender: row['Gender'] || '',
              dob: row['Date of Birth'] || '',
              sid: row['Student ID'] || '',
              currentClass: row['Current Class'] || '',
              classNumber: row['Class Number'] || '',
              schoolId: activeSchoolId,
              hasLoginAccess: hasLoginAccess
            }, { merge: true });

            count++;
          }

          await batch.commit();
          alert(t('studentMgt.imported', { count }));
          fetchStudents();
        } catch (err) {
          alert(t('studentMgt.importFailed', { message: err.message }));
          setLoading(false);
        }
      },
      error: (err) => alert(t('studentMgt.parseFailed', { message: err.message }))
    });
    };
    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%' }}>
      
      {/* Tabs */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'student', label: t('studentMgt.tab'), icon: GraduationCap }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            )
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 0', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
            <input
              type="text"
              placeholder={t('studentMgt.search')}
              value={globalSearch}
              onChange={e => setGlobalSearch(e.target.value)}
              style={{
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                outline: 'none',
                minWidth: '250px'
              }}
            />
          </div>
          <select
            value={topClassFilter}
            onChange={(e) => setTopClassFilter(e.target.value)}
            style={{
              padding: '0.75rem 1rem',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              outline: 'none',
              backgroundColor: 'var(--color-bg-card)'
            }}
          >
            <option value="">{t('studentMgt.allClasses')}</option>
            {(dropdownConfig.classes || []).filter(c => !c.sub_group && c.active !== false).map(c => (
              <option key={c.code} value={c.code}>{c.nameEn || c.name}</option>
            ))}
          </select>
          {selectedRows.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0 0.5rem', borderRight: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-text-main)' }}>{selectedRows.length} selected</span>
              <button className="btn-outline" onClick={() => setShowBatchModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Edit2 size={18} /> Batch Edit
              </button>
              <button className="btn-primary" onClick={handleBatchDelete} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--color-danger)' }}>
                <Trash2 size={18} /> Delete Selected
              </button>
            </div>
          )}
          <button className="btn-outline" onClick={exportStudentCSV} title={t('studentMgt.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Download size={18} />
          </button>
          <label className="btn-outline" title={t('studentMgt.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
            <Upload size={18} />
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={importStudentCSV} />
          </label>
          <button className="btn-primary" onClick={handleOpenAdd} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Plus size={18} /> Add
          </button>
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        
        {activeTab !== 'batch' && (
          <>
            {/* Data Table */}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--color-text-muted)' }}>
                  <RefreshCw className="animate-spin" size={24} style={{ marginRight: '0.5rem' }} /> Fetching...
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ backgroundColor: 'var(--color-bg-app)', userSelect: 'none' }}>
                    
                      <tr>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', width: '40px' }}>
                          <input 
                            type="checkbox" 
                            checked={sortedStudents.length > 0 && selectedRows.length === sortedStudents.length}
                            onChange={handleSelectAll}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.sid ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('sid')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>{t('studentMgt.sid')} {getSortIcon('sid')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.sid ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('sid')} />
                          </div>
                          {showFilters.sid && (
                            <input type="text" placeholder={t('studentMgt.filter')} value={filters.sid || ''} onChange={e => handleFilterChange('sid', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }} />
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.currentClass ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('class_classNo')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>{t('studentMgt.className')} {getSortIcon('class_classNo')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.currentClass ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('currentClass')} />
                          </div>
                          {showFilters.currentClass && (
                            <input type="text" placeholder={t('studentMgt.filter')} value={filters.currentClass || ''} onChange={e => handleFilterChange('currentClass', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }} />
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.classNumber ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('classNumber')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>{t('studentMgt.classNo')} {getSortIcon('classNumber')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.classNumber ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('classNumber')} />
                          </div>
                          {showFilters.classNumber && (
                            <input type="text" placeholder={t('studentMgt.filter')} value={filters.classNumber || ''} onChange={e => handleFilterChange('classNumber', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }} />
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.name ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('name')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>{t('studentMgt.name')} {getSortIcon('name')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.name ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('name')} />
                          </div>
                          {showFilters.name && (
                            <input type="text" placeholder={t('studentMgt.filter')} value={filters.name || ''} onChange={e => handleFilterChange('name', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }} />
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.chineseName ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('chineseName')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>{t('studentMgt.chineseName')} {getSortIcon('chineseName')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.chineseName ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('chineseName')} />
                          </div>
                          {showFilters.chineseName && (
                            <input type="text" placeholder={t('studentMgt.filter')} value={filters.chineseName || ''} onChange={e => handleFilterChange('chineseName', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }} />
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.email ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('email')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>{t('studentMgt.email')} {getSortIcon('email')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.email ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('email')} />
                          </div>
                          {showFilters.email && (
                            <input type="text" placeholder={t('studentMgt.filter')} value={filters.email || ''} onChange={e => handleFilterChange('email', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }} />
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.gender ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('gender')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>{t('studentMgt.gender')} {getSortIcon('gender')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.gender ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('gender')} />
                          </div>
                          {showFilters.gender && (
                            <select value={filters.gender || ''} onChange={e => handleFilterChange('gender', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                              <option value="">{t('studentMgt.all')}</option>
                              <option value="Male">{t('studentMgt.genders.male')}</option>
                              <option value="Female">{t('studentMgt.genders.female')}</option>
                            </select>
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters.dob ? '0.5rem' : '0' }}>
                            <div onClick={() => requestSort('dob')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>DOB {getSortIcon('dob')}</div>
                            <Filter size={14} style={{ cursor: 'pointer', color: filters.dob ? 'var(--color-primary)' : 'var(--color-text-muted)' }} onClick={() => toggleFilter('dob')} />
                          </div>
                          {showFilters.dob && (
                            <input type="text" placeholder={t('studentMgt.filter')} value={filters.dob || ''} onChange={e => handleFilterChange('dob', e.target.value)} style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }} />
                          )}
                        </th>
                        <th style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', fontSize: '0.875rem', width: '100px' }}>{t('studentMgt.actions')}</th>
                      </tr>
                    
                  </thead>
                  <tbody>
                    {sortedStudents.map((profile) => (
                      <tr key={profile.id} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: selectedRows.includes(profile.id) ? 'var(--color-bg-app)' : 'var(--color-bg-card)', transition: 'background-color 0.2s' }}>
                        
                          <>
                            <td style={{ padding: '1rem 1rem' }}>
                              <input 
                                type="checkbox" 
                                checked={selectedRows.includes(profile.id)}
                                onChange={() => handleSelectRow(profile.id)}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '1rem 1rem', fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{profile.sid || 'N/A'}</td>
                            <td style={{ padding: '1rem 1rem', fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{profile.currentClass || t('studentMgt.unassigned')}</td>
                            <td style={{ padding: '1rem 1rem', fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{profile.classNumber || 'N/A'}</td>
                            <td style={{ padding: '1rem 1rem', fontWeight: '600', color: 'var(--color-text-main)' }}>{profile.lastName} {profile.firstName}</td>
                            <td style={{ padding: '1rem 1rem', fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{profile.chineseName || 'N/A'}</td>
                            <td style={{ padding: '1rem 1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {profile.id}
                                {profile.hasLoginAccess && <span title={t('studentMgt.loginEnabled')}>🔑</span>}
                              </div>
                            </td>
                            <td style={{ padding: '1rem 1rem', fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{profile.gender}</td>
                            <td style={{ padding: '1rem 1rem', fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{profile.dob || 'N/A'}</td>
                          </>
                        
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button className="btn-outline" onClick={() => handleOpenEdit(profile)} style={{ padding: '0.5rem' }}>
                              <Edit2 size={16} />
                            </button>
                            <button className="btn-outline" onClick={() => handleDelete(profile.id)} style={{ padding: '0.5rem', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {sortedStudents.length === 0 && (
                      <tr>
                        <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>
                          {t('studentMgt.noProfiles')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              {isEditing ? t('studentMgt.editProfile') : t('studentMgt.addProfile')}
            </h3>
            
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.emailLabel')}</label>
                  <input type="email" className="input-field" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} disabled={isEditing} placeholder={t('studentMgt.emailPlaceholder')} required />
                  {isEditing && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('studentMgt.emailSynced')}</span>}
                </div>
              </div>

              {/* Guardian login. The guardian is a separate account from the student: Parent Notice
                  matches on this address to decide whose notices a signed-in parent may sign, and
                  the student can never sign their own. */}
              <div style={{ backgroundColor: 'var(--color-bg-app)', padding: '1rem', borderRadius: 'var(--radius-md)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <strong style={{ fontSize: '0.875rem' }}>{t('studentMgt.guardianSection')}</strong>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.guardianEmail')}</label>
                    <input
                      type="email"
                      className="input-field"
                      value={formData.guardianEmail}
                      onChange={e => setFormData({ ...formData, guardianEmail: e.target.value })}
                      placeholder={t('studentMgt.guardianEmailHint')}
                    />
                  </div>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      Guardian Password {isEditing && '(Type to set/reset)'}
                    </label>
                    <input
                      type="password"
                      className="input-field"
                      value={formData.guardianPassword}
                      onChange={e => setFormData({ ...formData, guardianPassword: e.target.value })}
                      placeholder={t('studentMgt.guardianPasswordHint')}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.guardianEmail2')}</label>
                    <input
                      type="email"
                      className="input-field"
                      value={formData.guardianEmail2}
                      onChange={e => setFormData({ ...formData, guardianEmail2: e.target.value })}
                      placeholder={t('studentMgt.optional')}
                    />
                  </div>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      Second Guardian Password {isEditing && '(Type to set/reset)'}
                    </label>
                    <input
                      type="password"
                      className="input-field"
                      value={formData.guardianPassword2}
                      onChange={e => setFormData({ ...formData, guardianPassword2: e.target.value })}
                      placeholder={t('studentMgt.guardianPasswordHint')}
                    />
                  </div>
                </div>

                {formData.guardianEmail2.trim() !== '' && (
                  <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '600' }}>{t('studentMgt.mainContact')}</span>
                    {[
                      { id: 'first', label: formData.guardianEmail || t('studentMgt.firstGuardian') },
                      { id: 'second', label: formData.guardianEmail2 || t('studentMgt.secondGuardian') },
                    ].map(opt => (
                      <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="primaryGuardian"
                          checked={formData.primaryGuardian === opt.id}
                          onChange={() => setFormData({ ...formData, primaryGuardian: opt.id })}
                          style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                )}

                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  {t('studentMgt.guardianHint')}
                </span>
              </div>

              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', backgroundColor: 'var(--color-bg-app)', padding: '1rem', borderRadius: 'var(--radius-md)' }}>
                <input 
                  type="checkbox" 
                  id="hasLoginAccess"
                  checked={formData.hasLoginAccess} 
                  onChange={e => setFormData({...formData, hasLoginAccess: e.target.checked})} 
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="hasLoginAccess" style={{ fontWeight: '600', cursor: 'pointer', margin: 0 }}>{t('studentMgt.enableLogin')}</label>
              </div>

              {formData.hasLoginAccess && (
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      Password {isEditing && "(Type to Override)"}
                    </label>
                    <input 
                      type="password" 
                      className="input-field" 
                      value={formData.password} 
                      onChange={e => setFormData({...formData, password: e.target.value})} 
                      placeholder={isEditing ? "Leave blank to keep current" : "••••••••"} 
                      required={!isEditing} 
                    />
                    {isEditing && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('studentMgt.passwordForcesChange')}</span>}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.lastName')}</label>
                  <input type="text" className="input-field" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.firstName')}</label>
                  <input type="text" className="input-field" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} required />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.chineseName')}</label>
                  <input type="text" className="input-field" value={formData.chineseName || ''} onChange={e => setFormData({...formData, chineseName: e.target.value})} required />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.gender')}</label>
                  <select className="input-field" value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})}>
                    <option value="Male">{t('studentMgt.genders.male')}</option>
                    <option value="Female">{t('studentMgt.genders.female')}</option>
                  </select>
                </div>
                
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.dob')}</label>
                    <input type="date" className="input-field" value={formData.dob} onChange={e => setFormData({...formData, dob: e.target.value})} />
                  </div>
              </div>

              
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.studentId')}</label>
                      <input type="text" className="input-field" value={formData.sid} onChange={e => setFormData({...formData, sid: e.target.value})} required />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.currentClass')}</label>
                      <select className="input-field" value={formData.currentClass} onChange={e => setFormData({...formData, currentClass: e.target.value})} required>
                        <option value="">{t('studentMgt.selectClass')}</option>
                        {(dropdownConfig.classes || []).filter(c => !c.sub_group && c.active !== false).map(c => (
                          <option key={c.code} value={c.code}>{c.nameEn || c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.classNumber')}</label>
                      <input type="number" min="1" className="input-field" value={formData.classNumber} onChange={e => setFormData({...formData, classNumber: e.target.value})} />
                    </div>
                  </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>{isEditing ? t('studentMgt.saveProfile') : t('studentMgt.addProfile')}</button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* Modal for Batch Edit */}
      {showBatchModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              Batch Edit Profiles ({selectedRows.length} selected)
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem' }}>
              {t('studentMgt.batchHint')}
            </p>
            
            <form onSubmit={handleBatchSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.currentClass')}</label>
                  <select className="input-field" value={batchFormData.currentClass} onChange={e => setBatchFormData({...batchFormData, currentClass: e.target.value})}>
                    <option value="">{t('studentMgt.leaveBlankToSkip')}</option>
                    {(dropdownConfig.classes || []).filter(c => !c.sub_group && c.active !== false).map(c => (
                      <option key={c.code} value={c.code}>{c.nameEn || c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentMgt.gender')}</label>
                  <select className="input-field" value={batchFormData.gender} onChange={e => setBatchFormData({...batchFormData, gender: e.target.value})}>
                    <option value="">{t('studentMgt.leaveBlankToSkip')}</option>
                    <option value="Male">{t('studentMgt.genders.male')}</option>
                    <option value="Female">{t('studentMgt.genders.female')}</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                <button type="button" className="btn-outline" onClick={() => setShowBatchModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? t('common.saving') : t('studentMgt.applyBatch')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentMgt;
